import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { fetchDowntimeFilterOptions, fetchDowntimes } from '@/backend/downtimesApi';
import { getBrowserStorage } from '@/components/UI/tableScale.mjs';
import { buildDowntimeParams, defaultFilters, loadFilters, saveFilters } from './downtimeState.mjs';
import { filterSelectedResponsibleIds, filterSelectedWorkStatusIds, normalizeManagerOptions, normalizeWorkStatusOptions } from './downtimeOptions.mjs';

export function useDowntimes() {
  const storage = getBrowserStorage();
  const filters = reactive(loadFilters(storage));
  const items = ref([]);
  const totalItems = ref(0);
  const totalPages = ref(1);
  const loading = ref(false);
  const isFetching = ref(false);
  const hasError = ref(false);
  const workStatusOptions = ref([]);
  const managerOptions = ref([]);
  const optionsError = ref(false);
  let requestId = 0;
  let pollTimer = null;

  watch(filters, (value) => saveFilters(storage, value), { deep: true });

  const loadDowntimes = async ({ skipIfFetching = false } = {}) => {
    if (skipIfFetching && isFetching.value) return;
    const currentRequestId = ++requestId;
    if (!items.value.length) loading.value = true;
    isFetching.value = true;
    try {
      const data = await fetchDowntimes(buildDowntimeParams(filters));
      if (currentRequestId !== requestId) return;
      items.value = Array.isArray(data?.items) ? data.items : [];
      filters.page = Number(data?.current_page) || filters.page;
      totalPages.value = Math.max(1, Number(data?.total_pages) || 1);
      totalItems.value = Math.max(0, Number(data?.total_items) || 0);
      hasError.value = false;
    } catch (error) {
      if (currentRequestId === requestId) hasError.value = true;
      throw error;
    } finally {
      if (currentRequestId === requestId) {
        loading.value = false;
        isFetching.value = false;
      }
    }
  };

  const updateFilters = (patch) => {
    Object.assign(filters, patch, { page: 1 });
    loadDowntimes().catch(() => {});
  };

  const resetFilters = () => {
    const sorting = filters.sorting;
    Object.assign(filters, defaultFilters(), { sorting });
    loadDowntimes().catch(() => {});
  };

  const setPage = (page) => {
    filters.page = page;
    loadDowntimes().catch(() => {});
  };

  const setSorting = (sorting) => {
    filters.sorting = Array.isArray(sorting) && sorting.length
      ? [sorting[0]]
      : defaultFilters().sorting;
    filters.page = 1;
    loadDowntimes().catch(() => {});
  };

  const loadOptions = async () => {
    try {
      const payload = await fetchDowntimeFilterOptions();
      workStatusOptions.value = normalizeWorkStatusOptions(payload);
      managerOptions.value = normalizeManagerOptions(payload);
      filters.workStatusIds = filterSelectedWorkStatusIds(filters.workStatusIds, workStatusOptions.value);
      filters.responsibleIds = filterSelectedResponsibleIds(filters.responsibleIds, managerOptions.value);
      optionsError.value = false;
      return true;
    } catch {
      optionsError.value = true;
      return false;
    }
  };

  onMounted(() => {
    loadOptions().finally(() => loadDowntimes().catch(() => {}));
    pollTimer = setInterval(() => loadDowntimes({ skipIfFetching: true }).catch(() => {}), 30000);
  });

  onBeforeUnmount(() => {
    requestId += 1;
    if (pollTimer) clearInterval(pollTimer);
  });

  return {
    filters,
    items,
    totalItems,
    totalPages,
    loading,
    isFetching,
    hasError,
    workStatusOptions,
    managerOptions,
    optionsError,
    loadOptions,
    loadDowntimes,
    updateFilters,
    resetFilters,
    setPage,
    setSorting,
  };
}
